import { useCallback, useEffect, useState } from 'react';
import { api, ApiFailure, type Role, type WorkspaceDetail } from '../../api';
import { Notice } from '../Panels';
import { Field, Hint } from '../Hint';
import { useToast } from '../Toast';
import { useConfirm } from '../Confirm';

const ROLE_HINT =
  'Admins manage people, billing and settings. Members can edit reports. Viewers can only look.';

/**
 * Who is in the workspace: invite colleagues, decide what each of them can do,
 * and remove the ones who have left. Every change explains itself in toasts.
 */
export function People({ userId }: { userId: string }) {
  const toast = useToast();
  const { confirm } = useConfirm();
  const [detail, setDetail] = useState<WorkspaceDetail | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  const reload = useCallback(() => {
    api
      .workspace()
      .then(setDetail)
      .catch((error: unknown) => {
        if (error instanceof ApiFailure) setFailure(error);
      });
  }, []);

  useEffect(reload, [reload]);

  if (detail === null) {
    return (
      <>
        {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}
        <p className="muted">
          <span className="spinner" /> Loading…
        </p>
      </>
    );
  }

  async function changeRole(memberUserId: string, name: string, role: Role): Promise<void> {
    if (role === 'owner') return;
    try {
      await api.updateMemberRole(memberUserId, role);
      toast.success(`${name} is now ${articleFor(role)} ${role}.`);
      reload();
    } catch (error) {
      toast.error(error instanceof ApiFailure ? error.message : 'The role could not be changed.');
      reload();
    }
  }

  async function remove(memberUserId: string, name: string, role: Role): Promise<void> {
    const ok = await confirm({
      title: `Remove ${name}?`,
      body:
        role === 'owner'
          ? 'They are an owner of this workspace. Removing them takes away their access immediately.'
          : 'They will lose access to this workspace immediately. Their own account and other workspaces are not affected.',
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.removeMember(memberUserId);
      toast.success(`${name} has been removed from the workspace.`);
      reload();
    } catch (error) {
      toast.error(error instanceof ApiFailure ? error.message : 'The person could not be removed.');
    }
  }

  return (
    <>
      <header className="intro">
        <h2>
          People
          <Hint text="Everyone who can open this workspace. Invite a colleague and choose what they are allowed to do." />
        </h2>
        <p>
          {detail.members.length} of {detail.limits.members} seats used · invite a colleague to work in
          this workspace with you.
        </p>
      </header>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      <section className="card">
        <header>
          <h2>Who has access</h2>
          <span className="sub">{detail.members.length} people</span>
        </header>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>
                  Role <Hint text={ROLE_HINT} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {detail.members.map((member) => (
                <tr key={member.userId}>
                  <td>
                    {member.name}
                    {member.userId === userId && <span className="muted"> (you)</span>}
                  </td>
                  <td>{member.email}</td>
                  <td>
                    {member.role === 'owner' || member.userId === userId ? (
                      <span className="badge">{member.role}</span>
                    ) : (
                      <select
                        value={member.role}
                        onChange={(event) => {
                          const next = event.target.value as Role;
                          void changeRole(member.userId, member.name, next);
                        }}
                      >
                        <option value="admin">admin</option>
                        <option value="member">member</option>
                        <option value="viewer">viewer — read only</option>
                      </select>
                    )}
                  </td>
                  <td>
                    {member.role !== 'owner' && member.userId !== userId && (
                      <button
                        className="ghost tiny danger"
                        onClick={() => void remove(member.userId, member.name, member.role)}
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <InviteForm
        memberLimit={detail.limits.members}
        onInvited={() => {
          toast.success('They can now open this workspace.');
          reload();
        }}
      />
    </>
  );
}

function InviteForm({
  memberLimit,
  onInvited,
}: {
  memberLimit: number;
  onInvited: () => void;
}) {
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member' | 'viewer'>('member');
  const [busy, setBusy] = useState(false);

  async function invite(): Promise<void> {
    setBusy(true);
    try {
      await api.addMember({ email: email.trim(), role });
      setEmail('');
      onInvited();
    } catch (error) {
      toast.error(error instanceof ApiFailure ? error.message : 'The invitation could not be sent.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <header>
        <h2>Invite a teammate</h2>
        <span className="sub">they need a DataVantage account first</span>
      </header>
      <div className="row" style={{ alignItems: 'flex-end', marginTop: 4 }}>
        <Field
          label="Their email"
          hint="They need to have signed up already with this email address. Once added, the workspace appears in their account."
          className="grow"
        >
          <input
            type="email"
            value={email}
            placeholder="colleague@company.com"
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>
        <Field label="What can they do?" hint={ROLE_HINT}>
          <select value={role} onChange={(event) => setRole(event.target.value as typeof role)}>
            <option value="admin">Admin — manage people & billing</option>
            <option value="member">Member — edit reports</option>
            <option value="viewer">Viewer — look only</option>
          </select>
        </Field>
        <button className="primary" disabled={busy || email.trim() === ''} onClick={() => void invite()}>
          {busy ? <span className="spinner" /> : 'Add to workspace'}
        </button>
      </div>
      <p className="muted" style={{ marginTop: 10, marginBottom: 0 }}>
        This workspace allows up to {memberLimit} {memberLimit === 1 ? 'person' : 'people'} on its
        current plan.
      </p>
    </section>
  );
}

function articleFor(role: string): string {
  return role === 'admin' ? 'an' : 'a';
}
